import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router-dom-v5-compat";

const PageViewIdContext = createContext<string>("");

/**
 * Returns the current page view ID (a UUID that changes on each route change).
 * Must be used within a PageViewIdProvider.
 */
export const usePageViewId = () => useContext(PageViewIdContext);

/**
 * Provides a stable UUID per page view that refreshes on route changes.
 * Used by HTS Media to correlate ad requests within a single page view.
 */
export const PageViewIdProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const [pageViewId, setPageViewId] = useState(() => crypto.randomUUID());

  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setPageViewId(crypto.randomUUID());
    }
  }, [location.pathname]);

  return (
    <PageViewIdContext.Provider value={pageViewId}>
      {children}
    </PageViewIdContext.Provider>
  );
};
